import { useEffect, useState } from "react";
import { Wifi, WifiOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  startHealthMonitor,
  subscribeToHealthStatus,
  type NetworkHealthStatus,
} from "@/lib/network-health";

export const OnlineStatusIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [backendStatus, setBackendStatus] = useState<NetworkHealthStatus>("healthy");
  const [showIndicator, setShowIndicator] = useState(false);

  // Standard online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowIndicator(true);
      toast.success("Back online", {
        description: "Your connection has been restored",
      });
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
      toast.error("You're offline", {
        description: "Some features may be limited",
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Backend connectivity health monitor
  useEffect(() => {
    startHealthMonitor();

    const unsubscribe = subscribeToHealthStatus((result) => {
      setBackendStatus(result.status);

      if (result.status === "blocked") {
        setShowIndicator(true);
        toast.error("Network is blocking DentalFlow", {
          description:
            "Your Wi-Fi network appears to be blocking our services. Try switching to mobile data or a different network.",
          duration: 10000,
          id: "network-blocked",
        });
      } else if (result.status === "degraded") {
        setShowIndicator(true);
        toast.warning("Slow connection detected", {
          description:
            "Your network connection to DentalFlow is slow. Some features may take longer to load.",
          duration: 6000,
          id: "network-degraded",
        });
        setTimeout(() => setShowIndicator(false), 6000);
      } else if (result.status === "healthy") {
        // If we were previously blocked/degraded, show recovery
        if (backendStatus === "blocked" || backendStatus === "degraded") {
          toast.success("Connection restored", {
            description: "DentalFlow services are reachable again",
            id: "network-restored",
          });
        }
        setTimeout(() => setShowIndicator(false), 3000);
      }
    });

    return unsubscribe;
  }, []);

  if (!showIndicator && backendStatus === "healthy") return null;

  // Determine display state
  const isBlocked = isOnline && backendStatus === "blocked";
  const isDegraded = isOnline && backendStatus === "degraded";
  const isFullyOffline = !isOnline;

  if (!showIndicator && !isBlocked) return null;

  return (
    <div className="fixed top-4 right-4 z-50 pointer-events-none max-w-xs">
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg text-sm font-medium ${
          isBlocked
            ? "bg-warning text-warning-foreground"
            : isFullyOffline
              ? "bg-destructive text-destructive-foreground"
              : isDegraded
                ? "bg-warning text-warning-foreground"
                : "bg-success text-success-foreground"
        }`}
      >
        {isBlocked ? (
          <>
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <div>
              <span className="block font-semibold">Network Blocked</span>
              <span className="block text-xs opacity-90">
                Try mobile data or another Wi-Fi
              </span>
            </div>
          </>
        ) : isFullyOffline ? (
          <>
            <WifiOff className="h-4 w-4" />
            <span>Offline</span>
          </>
        ) : isDegraded ? (
          <>
            <AlertTriangle className="h-4 w-4" />
            <span>Slow Connection</span>
          </>
        ) : (
          <>
            <Wifi className="h-4 w-4" />
            <span>Online</span>
          </>
        )}
      </div>
    </div>
  );
};
